import {
  autoEmphasisKeys,
  type CaptionStyle,
  type CaptionsElement,
  type TimelineWord,
  transcriptWordKey,
} from "@cva/shared";
import { Highlighter, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { ColorButton } from "../ColorPicker";
import { type EditorStore, useEditor, useEditorApi } from "../store";
import { Row, Section, Slider, Toggle } from "../ui";
import { mergeWordOverrides, type WordPatches } from "./commands";
import { useCaptions } from "./data";

/**
 * Keywords in captions: emphasised words keep an accent for the whole caption
 * (shared/video/emphasis.ts). Stored as `emphasis` in the word corrections,
 * so every change is one undo step and syncs live like other corrections.
 */

/** Emphasises the words, or removes it when all of them are emphasised already. */
export function toggleEmphasis(
  store: EditorStore,
  words: readonly TimelineWord[],
  patches: WordPatches = {},
): boolean {
  const on = !words.every((word) => word.emphasis);
  const next: Record<string, NonNullable<WordPatches[string]>> = { ...patches };
  for (const word of words) next[word.key] = { ...next[word.key], emphasis: on || undefined };
  store.getState().update((draft) => mergeWordOverrides(draft, next));
  return on;
}

function parseKeywords(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[,;\n]/)
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) => entry.slice(0, 60)),
    ),
  ].slice(0, 100);
}

/** Text tab: automatic keywords, the keyword list and removing all emphasis. */
export function EmphasisBar() {
  const store = useEditorApi();
  const { words, pages } = useCaptions();
  const keywords = useEditor((s) => s.doc.timeline?.captions.keywords);
  const [draft, setDraft] = useState((keywords ?? []).join(", "));
  const [open, setOpen] = useState(false);
  useEffect(() => setDraft((keywords ?? []).join(", ")), [keywords]);
  const emphasised = words.filter((word) => word.emphasis && !word.hidden).length;

  const auto = () => {
    const transcriptKeys = new Set(words.map((word) => word.key));
    const keys = autoEmphasisKeys(pages, keywords ?? []).map((key) =>
      transcriptWordKey(key, transcriptKeys),
    );
    if (keys.length === 0) {
      toast.info("Keine neuen Schlüsselwörter gefunden.");
      return;
    }
    store
      .getState()
      .update((draft) =>
        mergeWordOverrides(draft, Object.fromEntries(keys.map((key) => [key, { emphasis: true }]))),
      );
    toast.success(
      `${keys.length} ${keys.length === 1 ? "Wort" : "Wörter"} hervorgehoben – ⌘Z macht es rückgängig`,
    );
  };
  const clear = () =>
    store.getState().update((draft) => {
      const overrides = draft.timeline?.captions.overrides ?? {};
      mergeWordOverrides(
        draft,
        Object.fromEntries(
          Object.keys(overrides)
            .filter((key) => overrides[key]?.emphasis)
            .map((key) => [key, { emphasis: undefined }]),
        ),
      );
    });
  const saveKeywords = () => {
    const next = parseKeywords(draft);
    const current = keywords ?? [];
    if (next.join("\n") === current.join("\n")) return;
    store.getState().update((doc) => {
      if (!doc.timeline) return;
      if (next.length > 0) doc.timeline.captions.keywords = next;
      else delete doc.timeline.captions.keywords;
    });
  };

  return (
    <div className="mx-2 mt-2 space-y-2 rounded-lg border border-zinc-100 bg-zinc-50/60 p-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={auto}
          title="Pro Untertitel höchstens ein Wort: Zahlen und Beträge, Wörter deiner Liste, sonst das längste inhaltliche Wort"
          className="inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-2 text-xs font-medium text-white hover:bg-brand-700 [&_svg]:size-3.5"
        >
          <Highlighter /> Schlüsselwörter automatisch hervorheben
        </button>
        {emphasised > 0 ? (
          <button
            type="button"
            onClick={clear}
            title={`Alle ${emphasised} Hervorhebungen entfernen`}
            aria-label="Alle Hervorhebungen entfernen"
            className="inline-flex size-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-200/70 [&_svg]:size-3.5"
          >
            <X />
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="text-[11px] text-zinc-500 hover:text-zinc-700"
      >
        Eigene Schlüsselwörter{keywords?.length ? ` (${keywords.length})` : ""} …
      </button>
      {open ? (
        <div className="space-y-1">
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={saveKeywords}
            onKeyDown={(event) => {
              if (event.key === "Enter") (event.currentTarget as HTMLInputElement).blur();
            }}
            placeholder="z. B. Kaleido, Sommerfest, Rabatt"
            className="h-8 text-xs"
          />
          <p className="text-[10px] leading-4 text-zinc-400">
            Mit Komma trennen – Marken- und Produktnamen werden bevorzugt hervorgehoben.
          </p>
        </div>
      ) : null}
      <p className="text-[10px] leading-4 text-zinc-400">
        Oder im Text Wörter markieren und „Hervorheben“ wählen.
      </p>
    </div>
  );
}

/** Style tab: colour (default: the accent), size and weight of keywords. */
export function EmphasisStyleFields({ element }: { element: CaptionsElement }) {
  const store = useEditorApi();
  const style = element.style;
  const setStyle = (patch: Partial<CaptionStyle>, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target?.type !== "captions") return;
        for (const [key, value] of Object.entries(patch) as [keyof CaptionStyle, unknown][]) {
          if (value === undefined) delete target.style[key];
          else Object.assign(target.style, { [key]: value });
        }
      },
      merge ? { merge: `captions-${element.id}-${merge}` } : undefined,
    );
  const scale = style.emphasisScale ?? 1;
  return (
    <Section title="Schlüsselwörter">
      <Row>
        <span className="w-20 shrink-0 text-xs text-zinc-500">Farbe</span>
        <ColorButton
          value={style.emphasisColor ?? style.highlightColor}
          onChange={(emphasisColor) => setStyle({ emphasisColor })}
        />
        {style.emphasisColor ? (
          <button
            type="button"
            onClick={() => setStyle({ emphasisColor: undefined })}
            title="Wieder die Akzentfarbe (mit der Marke verknüpft) verwenden"
            className="text-[11px] text-brand-700 hover:underline"
          >
            wie Akzent
          </button>
        ) : (
          <span className="text-[11px] text-zinc-400">wie Akzent</span>
        )}
      </Row>
      <Row>
        <span className="w-20 shrink-0 text-xs text-zinc-500">Größe</span>
        <Slider
          value={scale}
          min={1}
          max={1.4}
          step={0.01}
          onChange={(value) =>
            setStyle({ emphasisScale: value > 1.001 ? value : undefined }, "emphasis-scale")
          }
        />
        <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
          {Math.round(scale * 100)} %
        </span>
      </Row>
      <Toggle
        checked={Boolean(style.emphasisBold)}
        onChange={(checked) => setStyle({ emphasisBold: checked || undefined })}
        label="Fett"
      />
    </Section>
  );
}
